import { userFacingErrorMessage } from "@/utils/extract-rtk-error";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { MESSAGES_CLOSE_CONCILIATION } from "@/pages/conciliation/stores/conciliation/utils/messages";
import { getConciliationIdFromUrl } from "@/pages/conciliation/stores/utils/getConciliationId";
import { Button } from "components/ui/button";
import { AlertTriangle, CheckCircle2, Loader2, PartyPopper } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "../../../../../components/ui/use-toast";
import { useDialogModal } from "../../../../../hook/handle-modal/hooks/actions";
import {
  useCanCloseConciliationQuery,
  useCloseConciliationMutation,
} from "../../../../../services/store-conciliation";
import { DtefDivergenceWarning } from "../dtef/components/dtef-divergence-warning";
import { useDtefDivergentCategories } from "../dtef/hooks/use-dtef-divergent-categories";

export const CloseConciliation = () => {
  const { handleModal } = useDialogModal();
  const conciliationId = getConciliationIdFromUrl(window.location.href);

  const {
    data: canCloseRes,
    refetch: refetchCanClose,
    isLoading: isLoadingCanClose,
  } = useCanCloseConciliationQuery(conciliationId ?? "", {
    refetchOnMountOrArgChange: true,
    skip: !conciliationId,
  });

  const dtefDivergentCategories = useDtefDivergentCategories(
    canCloseRes?.dtef_comparison,
  );

  const hasDtefDivergence =
    canCloseRes?.dtef_comparison?.has_any_divergence === true;

  const [closeConciliation, { isLoading: isCloseLoading }] =
    useCloseConciliationMutation();

  const [warnings, setWarnings] = useState<string[]>([]);
  const [canClose, setCanClose] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<"divergences" | "evidences" | "completed" | null>(null);
  const [progress, setProgress] = useState(0);
  const [verificationCompleted, setVerificationCompleted] = useState(false);

  const handleSave = async () => {
    if (canCloseRes?.canClose) {
      try {
        if (isLoadingCanClose)
          return toast({
            variant: "destructive",
            description: "Aguarde a verificação das divergências!",
            duration: 5000,
          });

        await closeConciliation(conciliationId ?? "").unwrap();
        handleModal({
          isOpen: false,
        });
        toast(MESSAGES_CLOSE_CONCILIATION.CAN_CLOSE.SUCCESS);
        setTimeout(() => {
          window.location.href = "/conciliacao/lojas";
        }, 2000);
      } catch (error) {
        toast({
          ...MESSAGES_CLOSE_CONCILIATION.CAN_CLOSE.ERROR,
          description: userFacingErrorMessage(
            error,
            MESSAGES_CLOSE_CONCILIATION.CAN_CLOSE.ERROR.description
          ),
        });
        refetchCanClose();
      }
    } else {
      toast({
        variant: "destructive",
        description: "Ainda há divergências com pendências!",
        duration: 5000,
      });
      refetchCanClose();
    }
  };

  useEffect(() => {
    if (isLoadingCanClose) {
      setVerificationCompleted(false);
      setCurrentStep("divergences");
      setProgress(50);
    } else if (canCloseRes) {
      setWarnings(canCloseRes?.messages || []);
      setCanClose(canCloseRes?.canClose || false);
      setCurrentStep("completed");
      setProgress(100);
      setVerificationCompleted(true);
    } else {
      setCurrentStep(null);
      setProgress(0);
      setVerificationCompleted(false);
    }
  }, [isLoadingCanClose, canCloseRes]);

  const hasPendingItems = !canClose || warnings.length > 0;

  return (
    <div className="max-h-[90vh] overflow-auto relative flex flex-col items-center justify-center p-2">
      <div className="w-full max-w-[500px] space-y-6">
        {!verificationCompleted && (
          <div className="space-y-4">
            <Progress value={progress} className="h-2" />

            <div className="space-y-3">
              <div
                className={cn(
                  "flex items-center gap-3 p-3 rounded-lg transition-all",
                  currentStep === "divergences" && "bg-amber-50",
                  (currentStep === "evidences" || currentStep === "completed") && "opacity-50",
                )}
              >
                {currentStep === "divergences" ? (
                  <Loader2 className="h-5 w-5 animate-spin text-[#F5CB2B]" />
                ) : currentStep === "evidences" || currentStep === "completed" ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
                )}
                <span className="font-medium">Verificando divergências...</span>
              </div>

              <div
                className={cn(
                  "flex items-center gap-3 p-3 rounded-lg transition-all",
                  currentStep === "evidences" && "bg-amber-50",
                  currentStep === "completed" && "opacity-50",
                )}
              >
                {currentStep === "evidences" ? (
                  <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                ) : currentStep === "completed" ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
                )}
                <span className="font-medium">Verificando emails pendentes...</span>
              </div>
            </div>
          </div>
        )}

        {verificationCompleted && (
          <div className="space-y-4">
            <div
              className={cn(
                "flex items-center justify-center gap-3 p-4 rounded-xl",
                hasPendingItems ? "" : "bg-green-50",
              )}
            >
              {hasPendingItems ? (
                <AlertTriangle className="h-8 w-8 text-amber-600" />
              ) : (
                <PartyPopper className="h-8 w-8 text-green-600" />
              )}
              <div>
                <h3
                  className={cn(
                    "font-bold text-lg",
                    hasPendingItems ? "text-amber-900" : "text-green-900",
                  )}
                >
                  {hasPendingItems ? "Resolva as Pendências!" : "Tudo Pronto!"}
                </h3>
                <p
                  className={cn(
                    "text-sm",
                    hasPendingItems ? "text-amber-700" : "text-green-700",
                  )}
                >
                  {hasPendingItems ? "Alguns itens precisam de atenção" : "Todos os requisitos foram atendidos"}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {canClose && hasDtefDivergence && (
                <div className="mt-4">
                  <DtefDivergenceWarning
                    divergentCategories={dtefDivergentCategories}
                  />
                </div>
              )}

              {warnings.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                      {warnings.length}
                    </span>
                    <span className="font-semibold text-amber-900">Avisos</span>
                  </div>
                  <div className="space-y-2">
                    {warnings.map((warning, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex-shrink-0 w-2 h-2 bg-amber-600 rounded-full mt-2"></div>
                        <p className="text-sm font-medium text-amber-900 whitespace-pre-line">{warning}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {verificationCompleted && (
        <div className="flex gap-3 pt-4 w-full max-w-[500px]">
          {!canClose && (
            <Button
              className="w-full"
              variant="outline"
              type="button"
              onClick={() => handleModal({ isOpen: false })}
            >
              Voltar
            </Button>
          )}
          {canClose && (
            <Button
              type="submit"
              className="w-full"
              disabled={!canClose || isCloseLoading || isLoadingCanClose}
              onClick={handleSave}
            >
              {isCloseLoading ? "Fechando..." : "Fechar Conciliação"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
